// Stage 19c — centralised error message mapping.
//
// The design audit's biggest single finding: error UIs were leaking
// raw error strings ("Failed to fetch", "design-audit forced 500",
// "401 Unauthorized") to end users. Every error-state render path
// must now go through `userMessage()` so the copy is consistent and
// actionable.
//
// Status detection PREFERS `err.status` when the error is an `ApiError` (the
// canonical shape the api.ts client now throws on non-2xx — see services/apiError.ts).
// The string-shape fallbacks below remain for legacy bare-Error throws and
// non-api errors: auth signals come as "Authentication expired" / "token revoked";
// network failures surface as TypeError "Failed to fetch"; a status may lead the
// message ("404: not found"). NOTE: the api client does NOT prefix its messages with
// the status, so a plain `String(err).includes('403')` check silently no-ops — that
// bug is exactly why ApiError.status exists.
//
// canRetry semantics:
//   - true  → the same fetch will probably succeed if retried (5xx,
//             network blip, generic). UI shows a "Retry" button.
//   - false → retry won't help (auth expired, 403, 404). UI shows the
//             appropriate alternative ("Sign in again" / "Go back").

import { ApiError } from "../services/apiError.js";

export interface UserMessage {
	title: string;
	description: string;
	canRetry: boolean;
	cta?: "retry" | "sign-in" | "go-back";
	/** The originating HTTP status, when one is known — the real
	 *  `ApiError.status` for an ApiError throw, or the status this branch
	 *  matched via the string-shape fallback for a non-ApiError throw (e.g.
	 *  a bare Error whose message happens to read "404: not found"). Left
	 *  undefined when there genuinely isn't one — a network failure has no
	 *  HTTP status at all — or when nothing more specific than the generic
	 *  fallback matched. Added for cos-error-state's page-failure signal
	 *  (the re-test fallback feature): a consumer that needs to react
	 *  differently to a dead record (404/410) vs a transient blip (5xx)
	 *  reads this instead of re-parsing the error itself. */
	status?: number;
}

export function userMessage(err: unknown): UserMessage {
	const msg = errorString(err).toLowerCase();
	// ApiError carries the HTTP status explicitly — prefer it; 0 means "not an
	// ApiError", so the string-shape fallbacks below take over for legacy throws.
	const status = err instanceof ApiError ? err.status : 0;

	// Auth expired / revoked — re-auth, no point retrying.
	if (
		status === 401 ||
		msg.includes("authentication expired") ||
		msg.includes("token revoked") ||
		msg.includes("session expired") ||
		startsWithStatus(msg, 401)
	) {
		return {
			title: "Your session has expired",
			description: "Sign in again to keep working.",
			canRetry: false,
			cta: "sign-in",
			status: status || 401,
		};
	}

	// Forbidden — user lacks permission. Re-trying with the same token
	// won't help.
	if (status === 403 || startsWithStatus(msg, 403) || msg.includes("forbidden")) {
		return {
			title: "You don't have permission to view this",
			description: "If you think this is a mistake, ask a teammate to check your access.",
			canRetry: false,
			cta: "go-back",
			status: status || 403,
		};
	}

	// Not found — entity-specific text where possible. The api.ts
	// wrapper appends the resource type to the error string for these
	// paths (e.g. "ticket not found", "partner 0: ...").
	if (status === 404 || startsWithStatus(msg, 404) || msg.includes("not found") || msg.includes("not_found")) {
		const entity = inferEntity(msg);
		return {
			title: entity ? `We couldn't find that ${entity}` : "We couldn't find what you were looking for",
			description: entity
				? `It may have been removed, or the link may be wrong.`
				: "It may have been removed, or the link may be wrong.",
			canRetry: false,
			cta: "go-back",
			status: status || 404,
		};
	}

	// Network / connectivity. fetch() throws TypeError on hard failures;
	// modern Chrome surfaces "Failed to fetch", Firefox "NetworkError
	// when attempting to fetch resource", Safari "The network connection
	// was lost". No HTTP status exists for these at all — status stays
	// undefined rather than guessing one.
	//
	// Responsive Workstream C — `retriableOffline` is checked FIRST when
	// present: api.ts sets it explicitly on a mutating request whose
	// fetch() itself rejected, which is the robust signal (the `.status`
	// precedent above — never string-sniff when an explicit field
	// exists). The message-text branches below remain as the fallback
	// for GET failures and any other bare-Error/non-api throw.
	if (
		(err instanceof ApiError && err.retriableOffline) ||
		msg.includes("failed to fetch") ||
		msg.includes("networkerror") ||
		msg.includes("network connection") ||
		msg.includes("network request failed")
	) {
		return {
			title: "Couldn't reach the server",
			description: "Check your connection and try again.",
			canRetry: true,
			cta: "retry",
		};
	}

	// 5xx server errors (including the design-audit's `forced 500` test
	// fixture). Generic "try in a moment" since the user's action is
	// likely fine.
	if (
		(status >= 500 && status <= 599) ||
		matchesStatusRange(msg, 500, 599) ||
		msg.includes("internal_error") ||
		msg.includes("forced 500")
	) {
		return {
			title: "Something went wrong",
			description: "Try again in a moment. If the problem keeps happening, let us know.",
			canRetry: true,
			cta: "retry",
			status: status || extractStatusDigits(msg),
		};
	}

	// Validation errors (422) — shouldn't reach a generic error UI in
	// practice (forms render field-level errors), but the catch-all
	// keeps user-facing copy clean if one slips through.
	if (status === 422 || startsWithStatus(msg, 422) || msg.includes("validation")) {
		return {
			title: "We couldn't process that request",
			description: "Some of the information looks invalid. Check the form and try again.",
			canRetry: false,
			status: status || 422,
		};
	}

	// Default — generic recoverable error. status carries through when the
	// throw was an ApiError with some OTHER code (e.g. 409, 429) that none
	// of the branches above recognised by name; otherwise there's nothing
	// more specific known than "something went wrong".
	return {
		title: "Something unexpected happened",
		description: "Try refreshing the page. If it keeps happening, let us know.",
		canRetry: true,
		cta: "retry",
		status: status || undefined,
	};
}

// errorString reaches into common shapes (Error, string, object with
// `message`) without throwing on weird inputs.
function errorString(err: unknown): string {
	if (err == null) return "";
	if (err instanceof Error) return err.message;
	if (typeof err === "string") return err;
	if (typeof err === "object" && "message" in err) {
		const m = (err as { message?: unknown }).message;
		if (typeof m === "string") return m;
	}
	return String(err);
}

// startsWithStatus matches error strings that lead with an HTTP status
// (e.g. "404: not found", "401 Unauthorized"). Looks at the first 8
// chars to keep it cheap.
function startsWithStatus(msg: string, code: number): boolean {
	const head = msg.slice(0, 8);
	return head.includes(String(code));
}

function matchesStatusRange(msg: string, lo: number, hi: number): boolean {
	const head = msg.slice(0, 8);
	const m = head.match(/(\d{3})/);
	if (!m) return false;
	const n = Number(m[1]);
	return n >= lo && n <= hi;
}

// Pulls a leading 3-digit status out of a non-ApiError message (e.g.
// "500: internal_error") for the `status` field on UserMessage — the
// numeric twin of matchesStatusRange's boolean check. Returns undefined for
// a message matched by keyword alone (e.g. "design-audit forced 500", where
// "500" isn't in the first 8 chars) rather than mis-reporting a status.
function extractStatusDigits(msg: string): number | undefined {
	const head = msg.slice(0, 8);
	const m = head.match(/(\d{3})/);
	return m ? Number(m[1]) : undefined;
}

// inferEntity reads obvious nouns out of the error message so 404 copy
// can be specific. Conservative — only matches well-known entities;
// otherwise returns "" so the caller falls back to generic copy.
function inferEntity(msg: string): string {
	if (msg.includes("ticket")) return "ticket";
	if (msg.includes("partner")) return "partner";
	if (msg.includes("project")) return "project";
	if (msg.includes("article") || msg.includes("kb")) return "article";
	if (msg.includes("contact")) return "contact";
	return "";
}
